/**
 * Agent Diagnostic Dialog
 * Small drop-in dialog that calls /api/admin/agents/_diagnostics/by-email and
 * renders a human-readable breakdown of where an agent email lives across every
 * data source (merchant REST API, db_business, db_customers, agent_invites).
 *
 * Used from AgentsSection when an admin can't find an agent that supposedly
 * exists. Especially useful in production where local DB reads don't tell the
 * whole story (the merchant REST API is the source of truth).
 */
import { useState } from 'react';
import { Search, Loader2, X, AlertTriangle, CheckCircle2, Info, XCircle, ExternalLink, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function verdictTone(verdict) {
  if (!verdict) return { color: '#94a3b8', bg: 'bg-slate-500/15', icon: Info };
  if (verdict.startsWith('found_approved') || verdict.startsWith('found_pending')) return { color: '#10b981', bg: 'bg-emerald-500/15', icon: CheckCircle2 };
  if (verdict.startsWith('found_rejected')) return { color: '#f97316', bg: 'bg-orange-500/15', icon: AlertTriangle };
  if (verdict.startsWith('not_found') || verdict.startsWith('merchant_api_not_configured')) return { color: '#ef4444', bg: 'bg-red-500/15', icon: XCircle };
  return { color: '#eab308', bg: 'bg-yellow-500/15', icon: AlertTriangle };
}

function Section({ title, data }) {
  return (
    <div className="bg-[#0a0a0a] border border-white/5 rounded-xl p-3">
      <div className="text-xs font-bold text-cyan-400 mb-2">{title}</div>
      <pre className="text-[11px] text-white/70 whitespace-pre-wrap break-all leading-relaxed font-mono">
        {JSON.stringify(data ?? null, null, 2)}
      </pre>
    </div>
  );
}

export default function AgentDiagnosticDialog({ open, onClose, token }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const runDiagnostic = async () => {
    const clean = (email || '').trim().toLowerCase();
    if (!clean || !clean.includes('@')) {
      toast.error('أدخل بريدًا صحيحًا');
      return;
    }
    setLoading(true);
    setReport(null);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/agents/_diagnostics/by-email`,
        {
          params: { email: clean },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setReport(res.data);
    } catch (e) {
      toast.error('فشل الفحص: ' + (e?.response?.data?.detail || e?.message || 'unknown'));
    } finally {
      setLoading(false);
    }
  };

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(report, null, 2));
      toast.success('تم نسخ التقرير كامل');
    } catch (_) {
      // ignore
    }
  };

  if (!open) return null;

  const tone = verdictTone(report?.verdict);
  const Icon = tone.icon;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-6"
      onClick={onClose}
      data-testid="agent-diagnostic-overlay"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#111827] border border-cyan-500/30 rounded-2xl p-6"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        data-testid="agent-diagnostic-dialog"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-cyan-400" />
            <h3 className="text-lg font-bold text-white">فحص وكيل بالبريد</h3>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1" data-testid="agent-diagnostic-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-white/60 mb-4 leading-relaxed">
          يقوم هذا الفحص بالبحث عن الوكيل عبر كل مصادر البيانات: API منصة التجار،
          قاعدة بيانات التجار، قاعدة بيانات العملاء، ودعوات الوكلاء. مفيد جدًا لتشخيص
          حالة الوكيل الموجود عنده لكنه لا يظهر في لوحة الأدمن.
        </p>

        <div className="flex items-center gap-2 mb-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runDiagnostic()}
            placeholder="agent@example.com"
            className="flex-1 px-3 py-2 rounded-xl bg-[#0a0a0a] border border-white/10 text-white focus:border-cyan-500 focus:outline-none text-sm"
            data-testid="agent-diagnostic-email-input"
            dir="ltr"
          />
          <button
            onClick={runDiagnostic}
            disabled={loading || !email}
            className="px-4 py-2 rounded-xl bg-cyan-500 text-black font-bold hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-2"
            data-testid="agent-diagnostic-run"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            فحص
          </button>
        </div>

        {report && (
          <div className="space-y-3">
            {/* Verdict banner */}
            <div className={`p-3 rounded-xl border ${tone.bg}`} style={{ borderColor: `${tone.color}55` }} data-testid="agent-diagnostic-verdict">
              <div className="flex items-start gap-2">
                <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: tone.color }} />
                <div>
                  <div className="text-xs font-bold mb-1" style={{ color: tone.color }}>الحكم</div>
                  <div className="text-sm text-white leading-relaxed">{report.verdict}</div>
                </div>
              </div>
            </div>

            {/* Sections */}
            <Section title="البيئة (Env)" data={report.env} />
            <Section title="API منصة التجار (Merchant REST)" data={report.merchant_api} />
            <Section title="db_business.users" data={report.local_db_business_users} />
            <Section title="db_business.agent_requests" data={report.db_business_agent_requests} />
            <Section title="db_customers.users" data={report.local_db_customers_users} />
            <Section title="agent_invites (asb606_admin)" data={report.agent_invites} />

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
              <button
                onClick={copyReport}
                className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1.5"
                data-testid="agent-diagnostic-copy"
              >
                <Copy className="w-3 h-3" />
                نسخ التقرير كامل (JSON)
              </button>
              <a
                href={`${BACKEND_URL}/api/admin/agents/_diagnostics/by-email?email=${encodeURIComponent(email)}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-white/40 hover:text-white/70 flex items-center gap-1.5"
              >
                فتح كـ JSON خام
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
